const DAY_MS = 24 * 60 * 60 * 1000;

/** Rolling window for broker-initiated ad regenerations. */
export const CREATIVE_REGENERATION_WINDOW_MS = 30 * DAY_MS;

export type CreativeRegenerationAllowanceInput = {
  userRegenerationCount: number;
  userRegenerationWindowStartedAt?: Date | string | null;
  max: number;
  now?: Date;
};

export type CreativeRegenerationAllowance = {
  used: number;
  remaining: number;
  /** When the current window ends. Null when no window is active. */
  resetsAt: Date | null;
  next: {
    userRegenerationCount: number;
    userRegenerationWindowStartedAt: Date;
  };
};

function parseWindowStart(
  value: Date | string | null | undefined,
): Date | null {
  if (value == null || value === "") return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Broker regenerations allowed on one ad. A missing or expired window counts
 * as unused. The next write starts a new window at 1, or increments inside
 * the current one.
 */
export function creativeRegenerationAllowance(
  input: CreativeRegenerationAllowanceInput,
): CreativeRegenerationAllowance {
  const now = input.now ?? new Date();
  const max = Math.max(input.max, 0);
  const started = parseWindowStart(input.userRegenerationWindowStartedAt);
  const expired =
    started == null ||
    now.getTime() - started.getTime() >= CREATIVE_REGENERATION_WINDOW_MS;
  const used = expired ? 0 : Math.max(input.userRegenerationCount, 0);
  const remaining = Math.max(max - used, 0);
  const resetsAt =
    expired || started == null
      ? null
      : new Date(started.getTime() + CREATIVE_REGENERATION_WINDOW_MS);

  return {
    used,
    remaining,
    resetsAt,
    next: expired
      ? {
          userRegenerationCount: 1,
          userRegenerationWindowStartedAt: now,
        }
      : {
          userRegenerationCount: used + 1,
          userRegenerationWindowStartedAt: started ?? now,
        },
  };
}

export function formatCreativeRegenerationResetDate(date: Date): string {
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}
